<script lang="ts" module>
  import type { Product, ProductVariant, Sales } from '@agapi/mlc-core';
  import type { ProductPageConfig } from '@agapi/content-storyblok';
  import { AlternateLinks } from '@agapi/ui/alternate-links';

  export interface ProductLoadResponse {
    product: Product;
    productConfig: ProductPageConfig; // Assuming a generic type for productConfig
    relatedBlogPosts: any; // Assuming a generic type for relatedBlogPosts
  }
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { untrack } from 'svelte';

  import { float, stack } from '@agapi/ui/panda/patterns';
  import { JsonLd } from '@agapi/ui/json-ld';
  import { Section } from '@agapi/ui/section';
  import { css } from '@agapi/ui/panda/css';

  import { EVENTS, getCommerce, INVENTORY } from '../../services';
  import { getCookie } from '../../utils/cookies.client';
  import { getLocaleFromUrl, rewriteUrl } from '../../utils/locale.shared';
  import { calculatePercentDiscount } from '../../utils/pricing.server';

  import { CampaignBadge } from '../../components/campaign-badge';
  import { BlogCarousel } from '../../components/blog';

  import {
    Footer as ProductBlocksFooter,
    Header as ProductBlocksHeader,
    ImageGallery as ProductBlocksImageGallery,
    SimilarProducts as ProductBlocksSimilarProducts,
    InfoHeader as ProductBlocksInfoHeader,
    InfoActions as ProductBlocksInfoActions,
    InfoUsps as ProductBlocksInfoUsps,
    InfoAccordion as ProductBlocksInfoAccordion,
    InfoStock as ProductBlocksInfoStock,
    Collections as ProductBlocksCollections
  } from './_components';
  import { browser } from '$app/environment';

  const { data }: { data: ProductLoadResponse } = $props();

  // Now we can use data.productConfig directly instead of fetching it
  const usps = data.productConfig.uspList;

  const state: {
    stock: number;
    last30DaySales?: Sales;
    similarProducts: ProductVariant[];
    loading: boolean;
  } = $state({ stock: 0, similarProducts: [], loading: true });

  $effect(() => {
    if (data.product.id) {
      untrack(async () => {
        state.loading = true;

        const handle = page.params.slug;
        if (!handle) {
          return;
        }

        if (browser) {
          window.scrollTo(0, 0);
        }

        EVENTS.emit('product_view', data.product);

        // Initial values
        state.stock = data.product.variants[0].stock;
        state.last30DaySales = {
          endDate: new Date().toISOString(),
          amount: data.product.variants[0].price.amount
        };

        if (window.lipscore?.initWidgets) {
          window.lipscore.initWidgets();
          console.info('[Lipscore] Widget has been initiated');
        } else {
          console.warn('[Lipscore] Widget not available on the window object');
        }

        const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
        INVENTORY.get30DaySales(
          { sku: data.product.variants[0].sku },
          { languageCode, countryCode }
        ).then((sales) => {
          state.last30DaySales = sales.amount ? (sales as Sales) : state.last30DaySales;
        });

        const identifier = getCookie<string>('__agapi_identifier') ?? 'default';
        const COMMERCE = getCommerce(window.fetch);
        COMMERCE.getRelatedProducts(data.product.id, { limit: 5 }, getLocaleFromUrl(page.url), {
          userId: identifier
        }).then(({ items }) => {
          state.similarProducts = items;
        });
      });
    }
  });

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.product.name,
    image: data.product.variants[0].images[0]?.url,
    description: data.product.description.long,
    sku: data.product.variants[0].sku,
    brand: {
      '@type': 'Brand',
      name: data.product.brand.name
    },
    offers: {
      '@type': 'Offer',
      url: rewriteUrl(page, `${page.url.origin}/product/${data.product.handle}`),
      priceCurrency: data.product.variants[0].price.currency,
      price: (
        (data.product.variants[0].price.amount - (data.product.variants[0].price.discount ?? 0)) /
        100
      ).toFixed(2),
      availability:
        data.product.variants[0].stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock'
    }
  };
</script>

<svelte:head>
  <title>{data.product.meta.title ?? data.product.name}</title>
  <meta
    name="description"
    content={data.product.meta.description ?? data.product.description.short}
  />
  <link rel="canonical" href={`${page.url.origin}${page.url.pathname}`} />
  <meta property="og:title" content={data.product.meta.title ?? data.product.name} />
  <meta
    property="og:description"
    content={data.product.meta.description ?? data.product.description.short}
  />
  <meta property="og:image" content={data.product.variants[0].images[0]?.url} />
  <meta property="og:url" content={`${page.url.origin}/product/${data.product.handle}`} />
  <meta property="og:type" content="product" />
</svelte:head>

{#if data.product?.languageFullSlugs}
  <AlternateLinks links={data.product.languageFullSlugs} />
{/if}
<!-- Rich Snippet -->
<JsonLd data={schemaData} />

<!-- Product Header (breadcrumbs etc...) -->
<ProductBlocksHeader product={data.product} />

<!-- Product Info Section -->
<Section
  rootStyles={css.raw({ overflowX: 'visible', zIndex: 2 })}
  containerStyles={css.raw({ flexDirection: { base: 'column', md: 'row' }, gap: 'snug', px: 0 })}
>
  <!-- Left Section Side -->
  <div
    class={stack({
      position: 'relative',
      overflow: 'visible',
      minWidth: { base: '100%', md: '40%' },
      maxWidth: { base: '100%', md: '40%' },
      gap: '7'
    })}
  >
    {#if data.product.variants[0].price.discount}
      <CampaignBadge size="lg" css={float.raw({ zIndex: 9, mr: 7, mt: 4 })}>
        {calculatePercentDiscount(data.product.variants[0].price)}%
      </CampaignBadge>
    {/if}
    {#key data.product.variants}
      <ProductBlocksImageGallery variants={data.product.variants} />
    {/key}
    <ProductBlocksSimilarProducts
      similarProducts={state.similarProducts}
      css={css.raw({ hideBelow: 'lg' })}
    />
  </div>

  <!-- Right Section Side -->
  <div class={stack({ gap: 'snug', flexDirection: 'column', width: { base: '100%', md: '60%' } })}>
    <ProductBlocksInfoHeader product={data.product} />
    <ProductBlocksInfoStock stock={data.product.variants[0].stock} />
    {#key data.product}
      <ProductBlocksInfoActions product={data.product} />
    {/key}
    <ProductBlocksInfoUsps usps={usps || []} />
    <ProductBlocksCollections collections={data.product.collections} />
    <ProductBlocksInfoAccordion
      product={data.product}
      last30DaySales={state.last30DaySales?.amount}
    />
  </div>
</Section>

<!-- Similar Products (desktop) -->
<ProductBlocksSimilarProducts
  similarProducts={state.similarProducts}
  css={css.raw({ hideFrom: 'lg' })}
/>

<!-- Product Footer (reviews etc...) -->
<ProductBlocksFooter stock={state.stock} product={data.product} />

<!-- Related Blog Posts -->
{#if data.relatedBlogPosts?.posts?.length > 0}
  <BlogCarousel posts={data.relatedBlogPosts.posts} />
{/if}
